import {useState, useEffect} from 'react';

import TextField from "../fragments/form/TextField"
import FormGroup from "../fragments/form/FormGroup"
import CheckboxSelector from "../fragments/form/CheckboxSelector"
import ImageSelectorWithPreview from "../fragments/form/ImageSelectorWithPreview"
import SubmitButton from "../fragments/form/SubmitButton"
import ColorPicker from "../fragments/form/ColorPicker"
import SearchSelector from '../components/SearchSelector/RegionSelect';
import GooglePlacesAutocomplete from "../components/Organizations/GooglePlacesAutocomplete";
import CountryApi from "../services/countries";
import StateApi from "../services/states";
import MunicipalityApi from "../services/municipalities";
import CircularProgress from "@mui/material/CircularProgress";

const BOOLEAN_FIELDS = new Set(['is_published']);

const normalizeBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
    }
    return false;
};

function CreateNetworkForm(props) {
    // Initialize formData with billing fields to ensure they're always present
    const [formData, setFormData] = useState({
        billing_information: '',
        billing_date: '',
        invoice_email: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const countryApi = new CountryApi(props.token);
    const stateApi = new StateApi(props.token);
    const municipalityApi = new MunicipalityApi(props.token);

    // Pre-populate form data if editing existing network
    useEffect(() => {
        if (props.network) {
            const newFormData = {
                // Initialize billing fields first
                billing_information: '',
                billing_date: '',
                invoice_email: ''
            };
            Object.keys(props.network).forEach(key => {
                if (props.network[key] !== null && props.network[key] !== undefined) {
                    if (BOOLEAN_FIELDS.has(key)) {
                        newFormData[key] = normalizeBoolean(props.network[key]) ? 1 : 0;
                    } else {
                        newFormData[key] = props.network[key];
                    }
                }
            });
            // Ensure billing fields are always set (use network value or empty string)
            const billingFields = ['billing_information', 'billing_date', 'invoice_email'];
            billingFields.forEach(field => {
                newFormData[field] = props.network[field] || '';
            });
            setFormData(newFormData);
        }
    }, [props.network]);

    const onChange = (id, value) => {
        setFormData(prev => {
            const normalizedValue = BOOLEAN_FIELDS.has(id) ? (value ? 1 : 0) : value;
            return {
                ...prev,
                [id]: normalizedValue
            };
        });
    }

    const onSubmit = (e) => {
        e.preventDefault();
        setIsSubmitted(true);
        
        // Validate that either subdomain or custom_domain is provided
        const subdomain = formData.subdomain;
        const customDomain = formData.custom_domain;
        
        if (!subdomain && !customDomain) {
            alert('Please provide either a subdomain or custom domain');
            return;
        }
        
        // Convert object to FormData for submission
        const formDataToSubmit = new FormData();
        // Fields that should not be sent to API (auto-managed by backend)
        const EXCLUDED_FIELDS = new Set(['id', 'created_at', 'updated_at', 'deleted_at']);
        
        Object.keys(formData).forEach(key => {
            // Skip excluded fields
            if (EXCLUDED_FIELDS.has(key)) {
                return;
            }
            
            if (formData[key] !== null && formData[key] !== undefined) {
                if (BOOLEAN_FIELDS.has(key)) {
                    const isTrue = normalizeBoolean(formData[key]);
                    formDataToSubmit.set(key, isTrue ? 'true' : 'false');
                } else {
                    // Include empty strings for text fields (they should be sent to API)
                    formDataToSubmit.set(key, formData[key]);
                }
            }
        });
        
        // Ensure billing fields are always included (even if empty)
        // Explicitly set them to ensure they're sent to the API
        const billingFields = ['billing_information', 'billing_date', 'invoice_email'];
        billingFields.forEach(field => {
            // Always set the field, even if it's empty - this ensures it's sent to the API
            const value = formData[field] !== undefined ? formData[field] : '';
            formDataToSubmit.set(field, value);
        });
        
        props.onSubmit(formDataToSubmit, props.location)
    }

  const updateLocationFields = (id, name, value) => {
    let newLocation = {...props.location}
    if (id === 'country') {
      newLocation.country_id = value;
      newLocation.country = name;
    }
    if (id === 'state') {
      newLocation.state_id = value;
      newLocation.state = name;
    }
    if (id === 'city') {
      newLocation.city_id = value;
      newLocation.city = name;
    }
    props.handleSetLocation(newLocation);
  }

  const resetLocationFields = (id) => {
    if (!props.location) {
      return;
    }

    let newLocation = { ...props.location };
    if (id === 'country') {
      newLocation.country = '';
      newLocation.country_id = null;
      newLocation.state = '';
      newLocation.state_id = null;
      newLocation.city = '';
      newLocation.city_id = null;
    } else if (id === 'state') {
      newLocation.state = '';
      newLocation.state_id = null;
      newLocation.city = '';
      newLocation.city_id = null;
    } else if (id === 'city') {
      newLocation.city = '';
      newLocation.city_id = null;
    }
    props.handleSetLocation(newLocation);
  };

  const onChangeLocation = (id, value) => {
    let newLocation = { ...props.location, [id]: value }
    props.handleSetLocation(newLocation);
  }

  const handlePlaceSelected = async(place) => {
    if (!place || !place.address_components || !place.geometry) {
      console.error("Invalid place object:", place);
      return;
  }
    const { address_components: components, geometry , formatted_address} = place;
    let state = "";
    let streetNumber = "";
    let route = "";
    let subpremise = "";

    const newLocation = { ...props.location };

    const lat = geometry?.location?.lat();
    const lng = geometry?.location?.lng();
  
    if (lat && lng) {
      newLocation.latitude = lat;
      newLocation.longitude = lng;
    }

    // First pass: extract address components
    for(let component of components){
       const { types, long_name } = component;
       
       if (types.includes("street_number")) {
         streetNumber = long_name;
       }
       
       if (types.includes("route")) {
         route = long_name;
       }
       
       if (types.includes("subpremise")) {
         subpremise = long_name;
       }
    }

    // Build address_1 from street_number + route, or use formatted_address as fallback
    if (streetNumber && route) {
      newLocation.address_1 = `${streetNumber} ${route}`;
    } else if (route) {
      newLocation.address_1 = route;
    } else {
      newLocation.address_1 = formatted_address;
    }

    // Set address2 only if subpremise exists
    if (subpremise) {
      newLocation.address2 = subpremise;
    } else {
      newLocation.address2 = "";
    }

    // Second pass: extract other location components
    for(let component of components){
       const { types, long_name } = component;
       
       if (types.includes("country")) {
            newLocation.country = long_name;
       }

      if (types.includes("administrative_area_level_1")) {
        state = component.long_name;
        newLocation.state = component.long_name;
      }

      if (types.includes("administrative_area_level_2") && !state) {
        state = component.long_name;
        newLocation.state = component.long_name;
      }

      if (types.includes("locality")) {
        newLocation.city = component.long_name;
      }

      if (types.includes("postal_code")) {
        newLocation.zip_code = component.long_name;
      }
    }
    
    setLoading(true);
    await getIds(newLocation)
    setLoading(false);
  };

  const getIds = async (locationData) => {
    try {
      let countryId = null;
      let stateId = null;
      let cityId = null;

      const newLocation = { ...locationData };
      let { country, state, city } = locationData

      if (country) {
        const countryResponse = await countryApi.getCountriesByName(country);
        const countryData = countryResponse.find(item =>
          item.country.trim().toLowerCase() === country.trim().toLowerCase()
        );
        if (countryData && countryData.id) {
          countryId = countryData?.id;
          newLocation.country_id = countryId
          newLocation.country = country
        } else {
          countryId = null;
          newLocation.country_id = null;
          props.handleSetLocation(newLocation);
          return;
        }
      }

      if (countryId && state) {
        const stateResponse = await stateApi.getStatesByName(countryId, state);
        const stateData = stateResponse.find(item =>
          item.state.trim().toLowerCase() === state.trim().toLowerCase()
        );
        if (stateData && stateData.id) {
          stateId = stateData?.id;
          newLocation.state_id = stateId
          newLocation.state = state
        } else {
          stateId = null;
          newLocation.state_id = null;
          props.handleSetLocation(newLocation);
          return;
        }
      }

      if (stateId && city) {
        const cityResponse = await municipalityApi.getCitiesByName(stateId, city);
        const cityData = cityResponse.find(item =>
          item.city.trim().toLowerCase() === city.trim().toLowerCase()
        );
        if (cityData && cityData.id) {
          cityId = cityData?.id;
          newLocation.city_id = cityId
          newLocation.city = city
        } else {
          cityId = null;
          newLocation.city_id = null
        }
      }
      props.handleSetLocation(newLocation);
    } catch (error) {
      console.error("Error:", error);
    }
  }

    return (
      <form onSubmit={onSubmit}>
            {/* Basic Network Information */}
            <h4>Network Information</h4>
            <TextField id="name" required={true} onChange={onChange} label="Name of Network (Displayed on Loadstone Mobile App)" onLoad={() => onChange("name", formData.name || "")} value={formData.name || ""} />
            <div style={{marginBottom: '10px'}}>
                <small className="text-muted">Choose either a subdomain OR custom domain (not both):</small>
            </div>
            <TextField id="subdomain" required={false} onChange={(id, value) => {
                onChange(id, value);
                if (value) {
                    onChange("custom_domain", ""); // Clear custom domain when subdomain is entered
                }
            }} label="Subdomain (e.g. 'chalkinglysimple' .loadstone.org)" onLoad={() => onChange("subdomain", formData.subdomain || "")} value={formData.subdomain || ""} />
            <TextField id="custom_domain" required={false} onChange={(id, value) => {
                onChange(id, value);
                if (value) {
                    onChange("subdomain", ""); // Clear subdomain when custom domain is entered
                }
            }} label="Custom Domain (e.g. customdomain.com)" onLoad={() => onChange("custom_domain", formData.custom_domain || "")} value={formData.custom_domain || ""} />
            <ImageSelectorWithPreview 
                id="image" 
                required={false} 
                label="Image 900x900 (Displayed on Loadstone Mobile App)" 
                onChange={onChange} 
                onLoad={() => onChange("image", null)}
                existingImageUrl={props.network?.image_url}
                network={props.network}
                previewWidth={200}
                previewHeight={200}
            />
            <FormGroup id="mission_statement" label="Mission Statement">
                <textarea
                    className="form-control"
                    rows={4}
                    name="mission_statement"
                    value={formData.mission_statement ?? ""}
                    onChange={(e) => onChange("mission_statement", e.target.value)}
                    placeholder="Enter the network mission statement..."
                />
            </FormGroup>
            {/* Background Color Selection */}
            <ColorPicker 
                id="background_color" 
                label="Choose Background Color" 
                onChange={onChange} 
                onLoad={() => onChange("background_color", formData.background_color || "#ffffff")} 
                value={formData.background_color || "#ffffff"}
            />

            {/* Address Information */}
            <h4>Address Information</h4>
            <div className='d-flex align-items-center'>
              <GooglePlacesAutocomplete onPlaceSelected={handlePlaceSelected} onChange={onChangeLocation} label="Address (Using Google Locator)" required={false} value={props.location ? props.location.address_1 : ''} />
              {loading && (
                <CircularProgress color="inherit" size={'small'} sx={{ width: '18px', height: '18px', marginLeft: '6px'}}/>
              )}
            </div>
            <TextField id="address2" required={false} onChange={onChangeLocation} label="Address 2" onLoad={() => onChangeLocation("address2","")}  value={props.location.address2 || ""} />
            <TextField id="city" required={false} onChange={onChangeLocation}  label="City" onLoad={() => onChangeLocation("city", "")}  value={props.location.city || ""} />
            <TextField id="state" required={false} onChange={onChangeLocation} label="State" onLoad={() => onChangeLocation("state","")}  value={props.location.state || ""}/>
            <TextField id="country" required={false} onChange={onChangeLocation}  label="Country" onLoad={() => onChangeLocation("country", "")} value={props.location.country || ""} />
            <TextField id="zip_code" required={false} onChange={onChangeLocation} label="Zip" onLoad={() => onChangeLocation("zip_code", "")}  value={props.location.zip_code || ""}/>
            
            {/* Region Selection for App Browsing */}
            <div className='create-network-wrapper col-md-10'>
            <h6 >Selected Region for in app Browsing:</h6>
            <SearchSelector
              id="country"
              required={false}
              isSubmitted={isSubmitted}
              location={props.location}
              collection={props.countries}
              updateLocationFields={updateLocationFields}
              handleSearchLocation={props.handleSearchLocation}
              resetLocationFields={resetLocationFields}
              label="Country"
              prompt="Please search and select a country" />
            <SearchSelector
              id="state"
              required={false}
              isSubmitted={isSubmitted}
              location={props.location}
              collection={props.states}
              updateLocationFields={updateLocationFields}
              handleSearchLocation={props.handleSearchLocation}
              resetLocationFields={resetLocationFields}
              label="State"
              prompt="Please select a country before select a state"
            />
            <SearchSelector
              id="city"
              required={false}
              isSubmitted={isSubmitted}
              location={props.location}
              collection={props.cities}
              updateLocationFields={updateLocationFields}
              handleSearchLocation={props.handleSearchLocation}
              resetLocationFields={resetLocationFields}
              label="City"
              prompt="Please select a state before select a city"
            />       
            </div>     
            <TextField id="longitude" type="number" required={false} onChange={onChangeLocation} label="Longitude" onLoad={() => onChangeLocation("longitude", "")} value={props.location.longitude || ""} />
            <TextField id="latitude" type="number" required={false} onChange={onChangeLocation} label="Latitude" onLoad={() => onChangeLocation("latitude","")}  value={props.location.latitude || ""}/>

            {/* Billing Information */}
            <h4>Billing Information</h4>
            <TextField id="billing_information" required={false} onChange={onChange} label="Billing Information" onLoad={() => onChange("billing_information", formData.billing_information || "")} value={formData.billing_information || ""} />
            <TextField id="billing_date" type="date" required={false} onChange={onChange} label="Billing Date" onLoad={() => onChange("billing_date", formData.billing_date || "")} value={formData.billing_date || ""} />
            <TextField id="invoice_email" type="email" required={false} onChange={onChange} label="Email for invoices" onLoad={() => onChange("invoice_email", formData.invoice_email || "")} value={formData.invoice_email || ""} />

            {/* Administrator Information */}
            <h4>Administrator Information</h4>
            <TextField id="admin_name" required={false} onChange={onChange} label="Name of Administrator" onLoad={() => onChange("admin_name", formData.admin_name || "")} value={formData.admin_name || ""} />
            <TextField id="admin_phone" required={false} onChange={onChange} label="Phone Number" onLoad={() => onChange("admin_phone", formData.admin_phone || "")} value={formData.admin_phone || ""} />
            <TextField id="admin_email" type="email" required={false} onChange={onChange} label="Administration e-mail address" onLoad={() => onChange("admin_email", formData.admin_email || "")} value={formData.admin_email || ""} />

            {/* Website */}
            <TextField id="website_url" required={false} onChange={onChange} label="Website (Displayed on Loadstone Mobile App)" onLoad={() => onChange("website_url", formData.website_url || "")} value={formData.website_url || ""} />

            {/* Publish Status */}
            <CheckboxSelector
                id="is_published"
                required={false}
                onChange={onChange}
                label="Publish"
                onLoad={() => onChange("is_published", normalizeBoolean(formData.is_published))}
                value={normalizeBoolean(formData.is_published)}
            />
            <SubmitButton />
        </form>
    );
}

export default CreateNetworkForm;
